import { useCallback, useContext, useState } from 'react';
import Modal from '@atlaskit/modal-dialog';

import DragAndDropList from 'components/DragAndDropList';
import AccountPickerContext from 'contexts/AccountPickerContext';

import Form from 'kingpin/forms/Form';
import FormHeader from 'kingpin/forms/FormHeader';
import FormContent from 'kingpin/forms/FormContent';
import Typography from 'kingpin/atoms/Typography';

import STORE from 'store';
import appNavItemTypeCheckers from 'types/appNavItemTypeCheckers';
import DragHandle from '../../../../../components/DragAndDropList/DragHandle';
import Row from '../../../../../components/Common/Row';
import styled from 'styled-components';
import Card from '../../../../../components/Common/Card';
import appTypeCheckers from '../../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider/appTypeCheckers';

const Item = styled(Card)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  cursor: pointer;
  height: 48px;
  padding: 16px 8px;
  background-color: var(--color-bg-primary);
  border: 1px solid var(--color-border-secondary);

  &:hover {
    background-color: var(--color-border-tertiary);
  }
`;

const SectionListItem = ({ item }: { item: V3.Navigation.Item }) => {
  if (appNavItemTypeCheckers.isBonusPicker(item)) {
    return null;
  }

  return (
    <Item>
      <Row centerAlign>
        <DragHandle />
        <Typography.Body type="Label">{item.label}</Typography.Body>
      </Row>
    </Item>
  );
};

const EditPinnedAssetsPopup = ({
  app,
  section,
  close,
}: {
  app: V3.App;
  section: V3.Navigation.SideSection;
  close: () => void;
}) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const [items, setItems] = useState<V3.Navigation.Item[]>(section.items);
  const onOrderChanged = useCallback(
    (newItems: V3.Navigation.Item[]) => {
      setItems(newItems);
      const newSection: V3.Navigation.SideSection = {
        ...section,
        items: newItems,
      };
      const newApp: V3.App = {
        ...app,
        navigationSettings: {
          ...app.navigationSettings,
          sections: app.navigationSettings.sections.map((s) => {
            if (s.key === section.key) {
              return newSection;
            }
            return s;
          }),
        },
      };

      if (appTypeCheckers.isWorkspace(newApp)) {
        STORE.contentDistributions
          .getWorkspacesRef({
            accountId: selectedAccountId,
          })
          .doc(newApp.id)
          .set(newApp);
      } else if (appTypeCheckers.isGeneralApp(newApp)) {
        STORE.contentDistributions
          .getGeneralAppsRef({
            accountId: selectedAccountId,
          })
          .doc(newApp.id)
          .set(newApp);
      } else if (appTypeCheckers.isBonusApp(newApp)) {
        STORE.contentDistributions
          .getBonusAppsRef({
            accountId: selectedAccountId,
          })
          .doc(newApp.id)
          .set(newApp);
      } else if (appTypeCheckers.isLegacyBonusApp(newApp)) {
        STORE.contentDistributions
          .getLegacyBonusAppsRef({
            accountId: selectedAccountId,
          })
          .doc(newApp.id)
          .set(newApp);
      }
    },
    [section, selectedAccountId, app],
  );

  return (
    <Modal onClose={close} shouldScrollInViewport={false} autoFocus={false}>
      <Form>
        <FormHeader title={'Manage Pinned Assets'} onClose={close} />
        <FormContent>
          <DragAndDropList
            items={items}
            onOrderChanged={onOrderChanged}
            droppableId={'pinned-assets-drop'}
            renderItem={(item) => <SectionListItem item={item} />}
          />
        </FormContent>
      </Form>
    </Modal>
  );
};

export default EditPinnedAssetsPopup;
